import { Copy, ExternalLink, Maximize2, Minimize2, Table2, TrendingUp } from 'lucide-react';
import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { formatValue, seriesColors } from '../lib/chartTheme';
import { humanStep } from '../lib/timeRange';

export interface ChartSeries {
  label: string;
  points: [number, number][];
}

export type AnnotationSeverity = 'critical' | 'high' | 'warning' | 'info';

/** An incident drawn as a band. `from`/`to` in epoch SECONDS, like the x axis. */
export interface ChartAnnotation {
  from: number;
  to: number;
  severity: AnnotationSeverity;
  title: string;
}

interface Props {
  title: string;
  help?: string;
  unit: string;
  series: ChartSeries[];
  unavailable?: string | null;
  loading?: boolean;
  /** New data is on its way; the old render stays, dimmed, rather than blanking. */
  refreshing?: boolean;
  /** The window that was ASKED for, so the axis spans it even when the data is sparse. */
  window?: { from: number; to: number } | null;
  step?: number | null;
  annotations?: ChartAnnotation[];
  expanded?: boolean;
  onToggleExpand?: () => void;
  onZoom?: (from: number, to: number) => void;
  onZoomOut?: () => void;
  exploreUrl?: string | null;
  query?: string | null;
}

/** Every chart on the screen shares one crosshair, keyed by time. */
const SYNC_KEY = 'wt-charts';
const BASE_HEIGHT = 190;
const EXPANDED_HEIGHT = 400;
const FONT = '11px ui-sans-serif, system-ui, sans-serif';

const cssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const withAlpha = (color: string, alpha: number) => {
  const hex = color.replace('#', '');
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
  const n = Number.parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(n)) return `rgba(128,128,128,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

// The same colours the incident board's left rule uses, so a band on a chart
// and a row on the Now screen read as one thing. Status colours, never series.
const severityColor = (severity: AnnotationSeverity) => {
  if (severity === 'critical') return cssVar('--danger') || '#dc2626';
  if (severity === 'high') return '#d97706';
  if (severity === 'warning') return cssVar('--warn') || '#b45309';
  return cssVar('--text-muted') || '#9ca3af';
};

const SEVERITY_RANK: Record<AnnotationSeverity, number> = { critical: 0, high: 1, warning: 2, info: 3 };

/** Overlapping bands folded into one interval each, carrying the worst severity. */
const mergeBands = (bands: ChartAnnotation[]): ChartAnnotation[] => {
  const merged: ChartAnnotation[] = [];
  for (const band of [...bands].sort((a, b) => a.from - b.from)) {
    const last = merged[merged.length - 1];
    if (last && band.from <= last.to) {
      last.to = Math.max(last.to, band.to);
      if (SEVERITY_RANK[band.severity] < SEVERITY_RANK[last.severity]) last.severity = band.severity;
    } else {
      merged.push({ ...band });
    }
  }
  return merged;
};

const stamp = (seconds: number, withDate: boolean) =>
  new Date(seconds * 1000).toLocaleString(undefined, {
    ...(withDate ? { month: 'short', day: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

/**
 * A time-series line chart, with the interaction layer a monitoring chart is
 * expected to have:
 *
 *   DRAG TO ZOOM. A horizontal drag on any chart becomes the window for EVERY
 *   chart -- the page owns the range, this component only reports the drag.
 *   The re-query at the finer step is what makes a zoom worth doing; uPlot's
 *   own client-side zoom would only stretch the points it already has.
 *
 *   ONE CROSSHAIR ACROSS THE SCREEN, and one tooltip with every visible series
 *   at that instant, values first. Hovering a chart moves the hairline on all
 *   of them, which is how a spike in one panel is read against the others.
 *
 *   A LEGEND YOU CAN CLICK. Click isolates a series, click again restores;
 *   cmd/ctrl-click toggles one. Hovering an entry lifts its line. Hidden series
 *   keep their colour -- colour follows the entity, never its rank.
 *
 *   INCIDENT BANDS. Whatever sphere-watch had open during the window is drawn
 *   as a tinted band, so the chart that explains an incident is read with the
 *   incident on it.
 *
 * And the choices the earlier version made stand: ONE axis, NOT stacked, 2px
 * lines on a hairline grid, gaps drawn as gaps because a hole means nobody was
 * looking, and a table view so no value is reachable only by hovering.
 */
export function Chart({
  title,
  help,
  unit,
  series,
  unavailable,
  loading,
  refreshing,
  window: win,
  step,
  annotations = [],
  expanded = false,
  onToggleExpand,
  onZoom,
  onZoomOut,
  exploreUrl,
  query,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const lastIdx = useRef<number | null>(null);
  // Callbacks and annotations are read through refs by uPlot's hooks, so a new
  // closure or a refreshed incident list never rebuilds the plot.
  const zoomRef = useRef(onZoom);
  zoomRef.current = onZoom;
  const zoomOutRef = useRef(onZoomOut);
  zoomOutRef.current = onZoomOut;
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;
  const hiddenRef = useRef<Set<number>>(new Set());

  const [showTable, setShowTable] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.getAttribute('data-theme') === 'dark');
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  const [cursorIdx, setCursorIdx] = useState<number | null>(null);
  const [focused, setFocused] = useState<number | null>(null);
  const [hovering, setHovering] = useState(false);
  const [over, setOver] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);
  hiddenRef.current = hidden;

  useEffect(() => {
    const observer = new MutationObserver(() =>
      setDark(document.documentElement.getAttribute('data-theme') === 'dark'),
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // uPlot wants one shared x array and one y array per series. Prometheus
  // returns each series on its own timestamps, so they are unioned first --
  // missing points become null, which is what draws the gap.
  const aligned = useMemo(() => {
    if (!series.length) return null;
    const stamps = [...new Set(series.flatMap((s) => s.points.map(([at]) => at)))].sort((a, b) => a - b);
    const index = new Map(stamps.map((at, i) => [at, i]));
    const columns = series.map((s) => {
      const column: (number | null)[] = new Array(stamps.length).fill(null);
      for (const [at, value] of s.points) {
        const at_ = index.get(at);
        if (at_ !== undefined) column[at_] = Number.isFinite(value) ? value : null;
      }
      return column;
    });
    return { x: stamps.map((at) => at / 1000), columns };
  }, [series]);

  const height = expanded ? EXPANDED_HEIGHT : BASE_HEIGHT;
  const windowFrom = win?.from;
  const windowTo = win?.to;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !aligned || showTable) return;
    const colors = seriesColors(dark);
    const axisColor = dark ? '#7e7e8c' : '#9ca3af';
    const gridColor = dark ? '#2c303b' : '#eceef2';
    const initialHidden = hiddenRef.current;

    const options: uPlot.Options = {
      width: host.clientWidth,
      height,
      padding: [12, 8, 0, 0],
      cursor: {
        y: false,
        points: { size: 7 },
        // The drag is reported to the page, never applied here: see the docblock.
        drag: { x: true, y: false, setScale: false },
        sync: { key: SYNC_KEY, setSeries: false, scales: ['x', null] },
        // The nearest line lifts on hover and the others recede.
        focus: { prox: 30 },
      },
      focus: { alpha: 0.3 },
      legend: { show: false },
      scales: {
        x: {
          time: true,
          // The axis is the window that was asked for. Letting the data set the
          // extent would let a series that stopped early hide that it stopped.
          ...(windowFrom !== undefined && windowTo !== undefined
            ? { range: () => [windowFrom, windowTo] as [number, number] }
            : {}),
        },
      },
      axes: [
        {
          stroke: axisColor,
          grid: { stroke: gridColor, width: 1 },
          ticks: { stroke: gridColor, width: 1 },
          font: FONT,
        },
        {
          stroke: axisColor,
          grid: { stroke: gridColor, width: 1 },
          ticks: { stroke: gridColor, width: 1 },
          font: FONT,
          size: 52,
          values: (_u, ticks) => ticks.map((tick) => formatValue(unit, tick)),
        },
      ],
      series: [
        { label: 'time' },
        ...series.map((s, index) => ({
          label: s.label,
          stroke: colors[index % colors.length],
          width: 2,
          show: !initialHidden.has(index),
          // Off on purpose: a hole means nobody was looking, and a joined line
          // would draw straight through it.
          spanGaps: false,
          points: { show: false },
        })),
      ],
      hooks: {
        setCursor: [
          (u) => {
            const idx = u.cursor.idx ?? null;
            // State moves only when the snapped point changes; the tooltip's
            // position follows the pointer through the DOM, without a render.
            if (idx !== lastIdx.current) {
              lastIdx.current = idx;
              setCursorIdx(idx);
            }
            const tip = tipRef.current;
            const left = u.cursor.left ?? -1;
            if (tip && left >= 0) {
              const flip = left > u.over.clientWidth * 0.6;
              const x = flip ? left - tip.offsetWidth - 14 : left + 14;
              const y = Math.max(0, Math.min(u.cursor.top ?? 0, u.over.clientHeight - tip.offsetHeight));
              tip.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
            }
          },
        ],
        setSeries: [
          (_u, seriesIdx, opts) => {
            const focus = (opts as { focus?: boolean | null }).focus;
            if (focus === undefined) return;
            setFocused(focus && seriesIdx !== null ? seriesIdx - 1 : null);
          },
        ],
        setSelect: [
          (u) => {
            const { left, width } = u.select;
            if (width < 4) return;
            const from = u.posToVal(left, 'x');
            const to = u.posToVal(left + width, 'x');
            u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
            zoomRef.current?.(from, to);
          },
        ],
        draw: [
          (u) => {
            const bands = annotationsRef.current;
            if (!bands.length) return;
            const { ctx } = u;
            const { left, top, width, height: plotHeight } = u.bbox;
            const dpr = devicePixelRatio || 1;
            const span = (band: ChartAnnotation) => {
              const x0 = Math.max(left, u.valToPos(band.from, 'x', true));
              const x1 = Math.min(left + width, u.valToPos(band.to, 'x', true));
              return x1 <= left || x0 >= left + width ? null : ([x0, Math.max(x1 - x0, 2 * dpr)] as const);
            };
            ctx.save();
            ctx.beginPath();
            ctx.rect(left, top, width, plotHeight);
            ctx.clip();
            // The tint is painted ONCE per stretch of time that had anything open,
            // in the worst severity of that stretch. Painting each incident's own
            // translucent band lets five overlapping incidents -- which is what a
            // real outage looks like -- compound into an opaque wall over the data.
            for (const merged of mergeBands(bands)) {
              const at = span(merged);
              if (!at) continue;
              ctx.fillStyle = withAlpha(severityColor(merged.severity), dark ? 0.13 : 0.09);
              ctx.fillRect(at[0], top, at[1], plotHeight);
            }
            // Each incident keeps its own rule along the top edge, so a band
            // under a flat line is still seen and two incidents still read as two.
            for (const band of bands) {
              const at = span(band);
              if (!at) continue;
              ctx.fillStyle = withAlpha(severityColor(band.severity), 0.85);
              ctx.fillRect(at[0], top, at[1], 2 * dpr);
            }
            ctx.restore();
          },
        ],
      },
    };

    const plot = new uPlot(options, [aligned.x, ...aligned.columns] as uPlot.AlignedData, host);
    plotRef.current = plot;
    lastIdx.current = null;
    setOver(plot.over);

    // The grid reflows when a neighbour expands, and a window resize is only
    // one of the ways this card changes width.
    const observer = new ResizeObserver(() => plot.setSize({ width: host.clientWidth, height }));
    observer.observe(host);
    const onDoubleClick = () => zoomOutRef.current?.();
    host.addEventListener('dblclick', onDoubleClick);

    return () => {
      observer.disconnect();
      host.removeEventListener('dblclick', onDoubleClick);
      plot.destroy();
      plotRef.current = null;
      setOver(null);
      setCursorIdx(null);
      setFocused(null);
    };
  }, [aligned, series, unit, dark, height, showTable, windowFrom, windowTo]);

  // Visibility is applied to the live plot rather than baked into it, so a
  // legend click never rebuilds the chart under the pointer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `over` changes when the plot is rebuilt, and a new plot has to be told which series are hidden
  useEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;
    series.forEach((_, index) => {
      const want = !hidden.has(index);
      if (plot.series[index + 1]?.show !== want) plot.setSeries(index + 1, { show: want });
    });
  }, [hidden, over, series]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new set of bands must be drawn onto the plot that exists; the plot is read through its ref
  useEffect(() => {
    plotRef.current?.redraw(false);
  }, [annotations]);

  const colors = seriesColors(dark);
  const latest = series.map((s) => s.points[s.points.length - 1]?.[1]);
  const valueAt = (index: number) =>
    cursorIdx !== null && aligned ? (aligned.columns[index][cursorIdx] ?? null) : (latest[index] ?? null);

  const toggleSeries = (index: number, event: MouseEvent) => {
    const isolate = !(event.metaKey || event.ctrlKey);
    setHidden((previous) => {
      if (isolate) {
        const alreadyAlone = !previous.has(index) && previous.size === series.length - 1;
        if (alreadyAlone) return new Set();
        return new Set(series.map((_, i) => i).filter((i) => i !== index));
      }
      const next = new Set(previous);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const focusSeries = (index: number | null) => {
    const plot = plotRef.current;
    if (!plot) return;
    if (index === null) plot.setSeries(null, { focus: false });
    else plot.setSeries(index + 1, { focus: true });
  };

  const copyQuery = () => {
    if (!query) return;
    void navigator.clipboard?.writeText(query).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const longStamps = !win || win.to - win.from > 86_400;
  const tooltip =
    hovering && cursorIdx !== null && aligned ? (
      <div ref={tipRef} className="wt-tip" role="status">
        <time>{stamp(aligned.x[cursorIdx], longStamps)}</time>
        {series
          .map((s, index) => ({ index, label: s.label, value: aligned.columns[index][cursorIdx] }))
          .filter((row) => !hidden.has(row.index) && row.value !== null)
          .sort((a, b) => (b.value as number) - (a.value as number))
          .map((row) => (
            <div key={row.label} className={`row${focused === row.index ? ' focus' : ''}`}>
              <span className="key" style={{ background: colors[row.index % colors.length] }} />
              <span className="val">{formatValue(unit, row.value)}</span>
              <span className="lbl">{row.label}</span>
            </div>
          ))}
        {annotations
          .filter((band) => band.from <= aligned.x[cursorIdx] && aligned.x[cursorIdx] <= band.to)
          .slice(0, 3)
          .map((band) => (
            <div key={`${band.from}-${band.title}`} className={`ann ann-${band.severity}`}>
              <span className="key" style={{ background: severityColor(band.severity) }} />
              {band.title}
            </div>
          ))}
      </div>
    ) : null;

  return (
    <section className={`wt-chart${expanded ? ' wt-chart-expanded' : ''}${refreshing ? ' is-refreshing' : ''}`}>
      <header>
        <h3>{title}</h3>
        <div className="wt-chart-actions">
          {series.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              title={showTable ? 'Show the chart' : 'Show as a table'}
              aria-label={showTable ? 'Show the chart' : 'Show as a table'}
              onClick={() => setShowTable((value) => !value)}
            >
              {showTable ? <TrendingUp size={14} /> : <Table2 size={14} />}
            </button>
          )}
          {query && (
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              title={copied ? 'Copied' : 'Copy the PromQL'}
              aria-label="Copy the PromQL"
              onClick={copyQuery}
            >
              <Copy size={14} />
            </button>
          )}
          {exploreUrl && (
            <a
              className="btn btn-ghost btn-icon"
              href={exploreUrl}
              target="_blank"
              rel="noreferrer"
              title="Explore this query in Grafana, same window"
              aria-label="Explore in Grafana"
            >
              <ExternalLink size={14} />
            </a>
          )}
          {onToggleExpand && (
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              title={expanded ? 'Back to the grid (Esc)' : 'Expand'}
              aria-label={expanded ? 'Collapse' : 'Expand'}
              onClick={onToggleExpand}
            >
              {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          )}
        </div>
      </header>
      {help && <p className="wt-chart-help">{help}</p>}

      {unavailable ? (
        // Stated, never a blank chart. An empty plot and an unreachable
        // Prometheus look identical, and one of them means "nothing is wrong".
        <div className="wt-chart-empty">{unavailable}</div>
      ) : loading ? (
        <div className="wt-chart-empty" style={{ height }}>
          Loading…
        </div>
      ) : !series.length ? (
        <div className="wt-chart-empty">
          No data in this range. Either nothing reported it, or Prometheus is not scraping that job.
        </div>
      ) : showTable ? (
        <table className="wt-table">
          <thead>
            <tr>
              <th>Series</th>
              <th style={{ width: 74, textAlign: 'right' }}>Latest</th>
              <th style={{ width: 74, textAlign: 'right' }}>Min</th>
              <th style={{ width: 74, textAlign: 'right' }}>Mean</th>
              <th style={{ width: 74, textAlign: 'right' }}>Max</th>
            </tr>
          </thead>
          <tbody>
            {series.map((s, index) => {
              const values = s.points.map(([, value]) => value).filter(Number.isFinite);
              const mean = values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
              return (
                <tr key={s.label}>
                  <td>
                    <span className="wt-swatch" style={{ background: colors[index % colors.length] }} />
                    {s.label}
                  </td>
                  <td className="wt-mono wt-num">{formatValue(unit, latest[index])}</td>
                  <td className="wt-mono wt-num">{formatValue(unit, Math.min(...values))}</td>
                  <td className="wt-mono wt-num">{formatValue(unit, mean)}</td>
                  <td className="wt-mono wt-num">{formatValue(unit, Math.max(...values))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <>
          <div
            className="wt-chart-body"
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
          >
            <div ref={hostRef} className="wt-chart-plot" />
            {over && tooltip && createPortal(tooltip, over)}
          </div>
          {/* Always present for two or more series, and it carries a value so
              identity is never colour alone: the latest, or the one under the
              crosshair while it is on the chart. */}
          {series.length > 1 && (
            <div className="wt-legend">
              {series.map((s, index) => (
                <button
                  key={s.label}
                  type="button"
                  className={hidden.has(index) ? 'off' : ''}
                  aria-pressed={!hidden.has(index)}
                  title="Click to show only this series; cmd/ctrl-click to toggle it"
                  onClick={(event) => toggleSeries(index, event)}
                  onMouseEnter={() => focusSeries(index)}
                  onMouseLeave={() => focusSeries(null)}
                >
                  <span className="wt-swatch" style={{ background: colors[index % colors.length] }} />
                  {s.label}
                  <b>{formatValue(unit, valueAt(index))}</b>
                </button>
              ))}
            </div>
          )}
          {series.length === 1 && (
            <div className="wt-legend">
              <span>
                <b style={{ fontSize: 15 }}>{formatValue(unit, valueAt(0))}</b>{' '}
                {cursorIdx !== null ? 'at the crosshair' : 'now'}
              </span>
            </div>
          )}
          <div className="wt-chart-meta">
            {step ? <span>{humanStep(step)} resolution</span> : null}
            {hidden.size > 0 && (
              <button type="button" className="wt-linkish" onClick={() => setHidden(new Set())}>
                {hidden.size} hidden · show all
              </button>
            )}
            <span className="wt-chart-hint">drag to zoom · double-click to zoom out</span>
          </div>
        </>
      )}
    </section>
  );
}
